import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { Dashboard } from '@/pages/Dashboard';
import { PortfolioView } from '@/pages/PortfolioView';
import { FleetInventory } from '@/pages/FleetInventory';
import { NodeDetail } from '@/pages/NodeDetail';
import { AllNodes } from '@/pages/AllNodes';
import { Networks } from '@/pages/Networks';
import { Services } from '@/pages/Services';
import { SecurityPolicies } from '@/pages/SecurityPolicies';
import { Users } from '@/pages/Users';
import { Settings } from '@/pages/Settings';
import { Triage } from '@/pages/Triage';
import { Login } from '@/pages/Login';
import { DevConsole } from '@/pages/DevConsole';
import { Overview as AnalyticsOverview } from '@/pages/Analytics/Overview';
import { ActiveUsers as AnalyticsActiveUsers } from '@/pages/Analytics/ActiveUsers';
import { ActiveEndpoints as AnalyticsActiveEndpoints } from '@/pages/Analytics/ActiveEndpoints';
import { SecurityPoliciesAnalytics } from '@/pages/Analytics/SecurityPolicies';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureVisibility } from '@/hooks/useFeatureVisibility';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, checking } = useAuth();

  if (checking) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function DevRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isDev, checking } = useAuth();

  if (checking) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isDev) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Redirects to /analytics/overview when the feature is hidden for the current
 * user. Used to wrap stage='base' routes so direct URL access (or bookmarks)
 * land on the MVP home rather than rendering a hidden page.
 */
function RouteGate({ feature, children }: { feature: string; children: React.ReactNode }) {
  const { isVisible } = useFeatureVisibility();
  if (!isVisible(feature)) {
    return <Navigate to="/analytics/overview" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Legacy route — redirect to new login */}
          <Route path="/onboarding" element={<Navigate to="/login" replace />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Default route: redirect to Analytics Overview (MVP home) for
                consumers; Neeve employees still land on the Dashboard via the
                RouteGate fall-through (visible → render Dashboard). */}
            <Route index element={<RouteGate feature="route.dashboard"><Dashboard /></RouteGate>} />
            <Route path="triage" element={<RouteGate feature="route.triage"><Triage /></RouteGate>} />
            <Route path="portfolios" element={<RouteGate feature="route.portfolios"><PortfolioView /></RouteGate>} />
            <Route path="fleet" element={<RouteGate feature="route.fleet"><FleetInventory /></RouteGate>} />
            <Route path="networks" element={<RouteGate feature="route.networks"><Networks /></RouteGate>} />
            <Route path="services" element={<RouteGate feature="route.services"><Services /></RouteGate>} />
            <Route path="security" element={<RouteGate feature="route.security"><SecurityPolicies /></RouteGate>} />
            <Route path="nodes" element={<RouteGate feature="route.nodes"><AllNodes /></RouteGate>} />
            <Route path="nodes/:id" element={<RouteGate feature="route.nodes"><NodeDetail /></RouteGate>} />
            <Route path="users" element={<RouteGate feature="route.users"><Users /></RouteGate>} />
            {/* Analytics — GoodData replacement (MVP, stage: mvp) — visible to everyone */}
            <Route path="analytics/overview" element={<AnalyticsOverview />} />
            <Route path="analytics/active-users" element={<AnalyticsActiveUsers />} />
            <Route path="analytics/active-endpoints" element={<AnalyticsActiveEndpoints />} />
            <Route path="analytics/security-policies" element={<SecurityPoliciesAnalytics />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="dev-console"
              element={
                <DevRoute>
                  <DevConsole />
                </DevRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
